<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');

const CHANNEL_ORDER = [
  'Channel::WebWidget',
  'Channel::Telegram',
  'Channel::Line',
  'Channel::Whatsapp',
  'Channel::FacebookPage',
  'Channel::Email',
  'Channel::Instagram',
  'Channel::Api',
  'Channel::TwilioSms',
  'Channel::Voice',
];

const CHANNEL_LABELS = {
  'Channel::WebWidget': 'Website',
  'Channel::FacebookPage': 'Messenger',
  'Channel::Whatsapp': 'WhatsApp',
  'Channel::TwilioSms': 'SMS',
  'Channel::Email': 'Email',
  'Channel::Api': 'API',
  'Channel::Telegram': 'Telegram',
  'Channel::Line': 'LINE',
  'Channel::Instagram': 'Instagram',
  'Channel::Voice': 'Voice',
};

const channelTypeLabel = type =>
  CHANNEL_LABELS[type] || type.replace('Channel::', '');

const inboxesList = computed(() => {
  let list =
    inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name)) || [];
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    list = list.filter(
      inbox =>
        inbox.name.toLowerCase().includes(query) ||
        inbox.channel_type?.toLowerCase().includes(query)
    );
  }
  return list;
});

const groupedInboxes = computed(() => {
  const raw = {};
  inboxesList.value.forEach(inbox => {
    const type = inbox.channel_type || 'Other';
    if (!raw[type]) raw[type] = [];
    raw[type].push(inbox);
  });
  // Sort groups by preferred order
  const sorted = {};
  CHANNEL_ORDER.forEach(ch => {
    if (raw[ch]) sorted[ch] = raw[ch];
  });
  Object.keys(raw).forEach(ch => {
    if (!sorted[ch]) sorted[ch] = raw[ch];
  });
  return sorted;
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);
const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);
const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};
const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <div class="inbox-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="inbox-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <h1 class="inbox-header__title">{{ $t('INBOX_MGMT.HEADER') }}</h1>
      <router-link :to="{ name: 'settings_inbox_new' }">
        <button class="primary-action-btn">
          <span class="i-ph-plus-bold size-3.5" />
          {{ $t('SETTINGS.INBOXES.NEW_INBOX') }}
        </button>
      </router-link>
    </header>

    <!-- Description -->
    <div class="inbox-desc px-6 py-3">
      <p class="text-sm inbox-desc__text">{{ $t('INBOX_MGMT.DESCRIPTION') }}</p>
    </div>

    <!-- Search toolbar -->
    <div class="inbox-toolbar px-6 py-3">
      <div class="inbox-toolbar__search">
        <span
          class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 inbox-toolbar__icon pointer-events-none"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="search-input reset-base w-full pl-10 pr-4 py-2 text-sm rounded-lg"
          :placeholder="
            $t('INBOX_MGMT.LIST.SEARCH_INPUT_PLACEHOLDER') ||
            'Search inboxes...'
          "
        />
      </div>
      <span class="inbox-toolbar__count">{{ inboxesList.length }} inboxes</span>
    </div>

    <!-- Content -->
    <div class="inbox-content flex-1 overflow-y-auto px-6 pb-8">
      <!-- Loading -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <div class="loading-spinner" />
      </div>

      <!-- Empty State -->
      <div v-else-if="!inboxesList.length" class="empty-state">
        <div class="empty-icon-wrap">
          <span class="i-ph-tray-bold size-10 text-slate-400" />
        </div>
        <h3 class="text-lg font-semibold text-slate-800 mb-2">
          {{ $t('INBOX_MGMT.LIST.404') }}
        </h3>
        <p class="text-sm text-slate-500 mb-6">
          Create your first inbox to start receiving messages
        </p>
        <router-link :to="{ name: 'settings_inbox_new' }">
          <button class="primary-action-btn">
            <span class="i-ph-plus-bold size-3.5" />
            {{ $t('SETTINGS.INBOXES.NEW_INBOX') }}
          </button>
        </router-link>
      </div>

      <!-- Grouped Grid -->
      <div v-else class="space-y-8 pt-4">
        <div
          v-for="(items, channelType) in groupedInboxes"
          :key="channelType"
          class="channel-group"
        >
          <!-- Group header -->
          <div class="channel-group__header">
            <span class="channel-group__label">{{
              channelTypeLabel(channelType)
            }}</span>
            <span class="channel-group__count">{{ items.length }}</span>
          </div>

          <!-- Cards grid -->
          <div class="inbox-grid">
            <div
              v-for="inbox in items"
              :key="inbox.id"
              class="inbox-card group"
            >
              <!-- Card top: avatar + action buttons -->
              <div class="inbox-card__top">
                <div class="inbox-avatar">
                  <Avatar
                    v-if="inbox.avatar_url"
                    :src="inbox.avatar_url"
                    :name="inbox.name"
                    :size="36"
                    rounded-full
                  />
                  <ChannelIcon
                    v-else
                    class="size-5 text-slate-500"
                    :inbox="inbox"
                  />
                </div>
                <div
                  class="inbox-actions opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <router-link
                    :to="{
                      name: 'settings_inbox_show',
                      params: { inboxId: inbox.id },
                    }"
                  >
                    <button
                      class="action-btn"
                      :title="$t('INBOX_MGMT.SETTINGS')"
                    >
                      <span class="i-ph-gear-bold size-3.5" />
                    </button>
                  </router-link>
                  <button
                    class="action-btn action-btn--danger"
                    :title="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                    @click="openDelete(inbox)"
                  >
                    <span class="i-ph-trash-bold size-3.5" />
                  </button>
                </div>
              </div>

              <!-- Card meta -->
              <div class="inbox-card__meta">
                <h3 class="inbox-card__name">{{ inbox.name }}</h3>
                <p class="inbox-card__channel">
                  <ChannelName
                    :channel-type="inbox.channel_type"
                    :medium="inbox.medium"
                  />
                </p>
              </div>

              <!-- Card footer -->
              <div class="inbox-card__footer">
                <router-link
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                  class="inbox-card__footer-link"
                >
                  <span class="i-ph-gear-bold size-3" />
                  Configure
                  <span class="i-ph-arrow-right-bold size-3 ml-auto" />
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </div>
</template>

<style scoped>
/* ── Root ── */
.inbox-page,
.dark .inbox-page {
  background: #ffffff !important;
}

/* ── Header ── */
.inbox-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
}

.inbox-header__title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

/* ── Description ── */
.inbox-desc {
  border-bottom: 1px solid #e5e7eb;
}
.inbox-desc__text {
  color: #4b5563;
  margin: 0;
}

/* ── Toolbar ── */
.inbox-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.inbox-toolbar__search {
  position: relative;
  max-width: 280px;
  width: 100%;
}

.inbox-toolbar__icon {
  color: #9ca3af;
}

.inbox-toolbar__count {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
}

/* ── Search input ── */
.search-input {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.search-input:focus {
  outline: none;
  border-color: #d1d5db !important;
  background: #ffffff !important;
}
.search-input::placeholder {
  color: #9ca3af !important;
}

/* ── Add Inbox button ── */
.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;
}
.primary-action-btn:hover {
  background: #1f2937;
}

/* ── Content area ── */
.inbox-content {
  background: #ffffff;
}

/* ── Loading ── */
.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.empty-icon-wrap {
  width: 88px;
  height: 88px;
  border-radius: 20px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

/* ── Channel group ── */
.channel-group {
  /* each group section */
}

.channel-group__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.channel-group__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}

.channel-group__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 4px;
}

/* ── Grid ── */
.inbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 12px;
}

/* ── Card ── */
.inbox-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  min-height: 148px;
  transition:
    border-color 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.inbox-card:hover {
  border-color: #d1d5db;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.inbox-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}

.inbox-avatar {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.inbox-card__meta {
  flex: 1;
}

.inbox-card__name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inbox-card__channel {
  font-size: 11px;
  color: #6b7280;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Card footer ── */
.inbox-card__footer {
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #f3f4f6;
}

.inbox-card__footer-link {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #6b7280;
  font-size: 12px;
  text-decoration: none;
  transition: color 0.15s ease;
}
.inbox-card__footer-link:hover {
  color: #111827;
}

/* ── Action buttons ── */
.inbox-actions {
  display: flex;
  gap: 5px;
}

.action-btn {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-btn:hover {
  background: #f3f4f6;
  color: #111827;
}
.action-btn--danger:hover {
  background: #fef2f2;
  color: #ef4444;
  border-color: #fecaca;
}
</style>
